import type { HeightBucket } from "@/lib/listings/builder-state";

interface ProductDiagramProps {
  productTypeSlug: string;
  footprint: string | null;
  heightBucket: HeightBucket | null;
  walls: number | null;
}

type Point = [number, number];

const CENTER_X = 290;
const TOP_Y = 84;
const RADIUS_X = 120;
const RADIUS_Y = 26;
const FACE_SHADES = ["#b9945f", "#c9a56f", "#d6b57f", "#c39d67"];

const round = (value: number) => Math.round(value * 10) / 10;

function points(list: Point[]): string {
  return list.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
}

function octagon(y: number, radius: number, widthScale: number): Point[] {
  return Array.from({ length: 8 }, (_, index) => {
    const angle = Math.PI / 8 + (index * Math.PI) / 4;
    return [
      round(CENTER_X + Math.cos(angle) * radius * widthScale),
      round(y + (Math.sin(angle) * radius * RADIUS_Y * widthScale) / RADIUS_X),
    ];
  });
}

function isFrontEdge(start: Point, end: Point, y: number): boolean {
  return start[1] + end[1] >= y * 2 - 1;
}

function widthScaleFor(footprint: string | null): number {
  if (!footprint) return 1;
  const length = Number(footprint.split("x")[0]);
  return Number.isNaN(length) ? 1 : Math.min(1, Math.max(0.8, length / 48));
}

function heightFor(bucket: HeightBucket | null): number {
  if (bucket === "under36") return 84;
  if (bucket === "36to44") return 112;
  return 132;
}

interface Geometry {
  scale: number;
  height: number;
  wallCount: number;
  top: Point[];
  bottom: Point[];
  inner: Point[];
  floor: Point[];
  palletY: number;
}

function geometry({ footprint, heightBucket, walls }: ProductDiagramProps): Geometry {
  const scale = widthScaleFor(footprint);
  const height = heightFor(heightBucket);
  const wallCount = walls ?? 3;
  return {
    scale,
    height,
    wallCount,
    top: octagon(TOP_Y, RADIUS_X, scale),
    bottom: octagon(TOP_Y + height, RADIUS_X, scale),
    inner: octagon(TOP_Y, RADIUS_X - (5 * wallCount) / 3, scale),
    floor: octagon(TOP_Y + height * 0.55, RADIUS_X - 7, scale),
    palletY: TOP_Y + height + 2,
  };
}

function Pallet({ palletY, scale }: Pick<Geometry, "palletY" | "scale">) {
  const deck = octagon(palletY + 5, 134, scale);
  const deckLow = octagon(palletY + 13, 134, scale);
  const radiusX = RADIUS_X * scale;
  return (
    <g>
      <ellipse cx={CENTER_X} cy={palletY + 32} rx={round(radiusX * 1.1 + 40)} ry={22} fill="url(#gaylord-shadow)" />
      <polygon points={points(deck)} fill="#cfa97a" stroke="#9c7a4d" strokeWidth={0.8} />
      {[-3, -2, -1, 0, 1, 2, 3].map((offset) => (
        <line
          key={offset}
          x1={round(CENTER_X + offset * radiusX * 0.31)}
          y1={round(palletY + 5 - RADIUS_Y * scale * 1.02)}
          x2={round(CENTER_X + offset * radiusX * 0.31)}
          y2={round(palletY + 5 + RADIUS_Y * scale * 1.02)}
          stroke="#a37f52"
          strokeWidth={1.2}
          opacity={0.7}
        />
      ))}
      {deck.flatMap((current, index) => {
        const next = deck[(index + 1) % 8];
        if (current[1] + next[1] < palletY * 2 + 11) return [];
        return [
          <polygon key={index} points={points([current, next, deckLow[(index + 1) % 8], deckLow[index]])} fill="#8a6a44" />,
        ];
      })}
      {[-0.62, 0, 0.62].map((offset) => (
        <rect
          key={offset}
          x={round(CENTER_X + offset * radiusX - 12)}
          y={round(palletY + 13 + RADIUS_Y * scale * 0.9)}
          width={24}
          height={10}
          fill="#5b4429"
        />
      ))}
    </g>
  );
}

function Flutes({ top, wallCount }: Pick<Geometry, "top" | "wallCount">) {
  return (
    <g stroke="#7f5f37" strokeWidth={1}>
      {top.flatMap((current, index) => {
        const next = top[(index + 1) % 8];
        if (!isFrontEdge(current, next, TOP_Y)) return [];
        const segments = Math.round(Math.hypot(next[0] - current[0], next[1] - current[1]) / 6);
        return Array.from({ length: Math.max(segments - 1, 0) }, (_, step) => {
          const x = round(current[0] + ((next[0] - current[0]) * (step + 1)) / segments);
          const y = round(current[1] + ((next[1] - current[1]) * (step + 1)) / segments);
          return <line key={`${index}-${step}`} x1={x} y1={y} x2={x} y2={y + 3 + wallCount} />;
        });
      })}
    </g>
  );
}

function Body({ top, bottom, inner, floor, wallCount }: Geometry) {
  return (
    <g>
      <polygon points={points(top)} fill="url(#gaylord-interior)" />
      {inner.flatMap((current, index) => {
        const next = inner[(index + 1) % 8];
        if (isFrontEdge(current, next, TOP_Y)) return [];
        return [
          <polygon
            key={index}
            points={points([current, next, floor[(index + 1) % 8], floor[index]])}
            fill={index % 2 ? "#a68355" : "#95744a"}
          />,
        ];
      })}
      <polygon points={points(floor)} fill="#6d5233" />
      {top.flatMap((current, index) => {
        const next = top[(index + 1) % 8];
        if (!isFrontEdge(current, next, TOP_Y)) return [];
        return [
          <polygon
            key={index}
            points={points([current, next, bottom[(index + 1) % 8], bottom[index]])}
            fill={FACE_SHADES[index % 4]}
            stroke="#8f6f3f"
            strokeWidth={0.8}
          />,
        ];
      })}
      <polygon points={points(top)} fill="none" stroke="#e3c896" strokeWidth={2 + wallCount * 1.5} strokeLinejoin="round" />
      <polygon points={points(inner)} fill="none" stroke="#7f5f37" strokeWidth={1} strokeDasharray="2 3" />
      <Flutes top={top} wallCount={wallCount} />
    </g>
  );
}

function GaylordDiagram(props: ProductDiagramProps) {
  const shape = geometry(props);
  return (
    <svg viewBox="0 0 560 330" role="img" aria-label="Gaylord box on a pallet" className="block h-auto w-full">
      <defs>
        <radialGradient id="gaylord-shadow" cx=".5" cy=".5" r=".5">
          <stop offset="0" stopColor="#1b2734" stopOpacity=".22" />
          <stop offset="1" stopColor="#1b2734" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="gaylord-interior" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#8f6f3f" />
          <stop offset="1" stopColor="#5a4327" />
        </linearGradient>
      </defs>
      <Pallet palletY={shape.palletY} scale={shape.scale} />
      <Body {...shape} />
    </svg>
  );
}

function GenericDiagram({ heightBucket }: ProductDiagramProps) {
  const height = heightFor(heightBucket);
  return (
    <svg viewBox="0 0 560 330" role="img" aria-label="Product outline" className="block h-auto w-full">
      <polygon points={`170,${300 - height} 410,${300 - height} 470,${260 - height} 230,${260 - height}`} fill="#d6b57f" />
      <rect x="170" y={300 - height} width="240" height={height} fill="#c9a56f" />
      <polygon points={`410,${300 - height} 470,${260 - height} 470,260 410,300`} fill="#b9945f" />
    </svg>
  );
}

export function ProductDiagram(props: ProductDiagramProps) {
  if (props.productTypeSlug === "gaylord-boxes") return <GaylordDiagram {...props} />;
  return <GenericDiagram {...props} />;
}
